// utils/weatherUtils.ts

export const getWeatherDescription = (code: number): string => {
  const weatherCodes: { [key: number]: string } = {
    0: 'Clear sky',
    1: 'Mainly clear',
    2: 'Partly cloudy',
    3: 'Overcast',
    45: 'Foggy',
    48: 'Depositing rime fog',
    51: 'Light drizzle',
    53: 'Moderate drizzle',
    55: 'Dense drizzle',
    61: 'Slight rain',
    63: 'Moderate rain',
    65: 'Heavy rain',
    71: 'Slight snow',
    73: 'Moderate snow',
    75: 'Heavy snow',
    77: 'Snow grains',
    80: 'Slight rain showers',
    81: 'Moderate rain showers',
    82: 'Violent rain showers',
    85: 'Slight snow showers',
    86: 'Heavy snow showers',
    95: 'Thunderstorm',
    96: 'Thunderstorm with slight hail',
    99: 'Thunderstorm with heavy hail',
  };

  return weatherCodes[code] || 'Unknown';
};

export type WeatherIconType =
  | 'blizzard'
  | 'clear-day'
  | 'clear-night'
  | 'fog'
  | 'haze'
  | 'heavy-rain'
  | 'light-rain'
  | 'light-snow'
  | 'mostly-cloudy'
  | 'overcast'
  | 'partly-cloudy-day'
  | 'partly-cloudy-night'
  | 'rain-moon'
  | 'rain-sun'
  | 'showers'
  | 'sleet'
  | 'snow-showers'
  | 'sunrise'
  | 'sunset'
  | 'thunderstorm-with-rain'
  | 'thunderstorm'
  | 'windy';

/**
 * Check if current time is during day (6 AM - 8 PM)
 */
const isDaytime = (): boolean => {
  const hour = new Date().getHours();
  return hour >= 6 && hour < 20;
};

export const getWeatherIconType = (code: number): WeatherIconType => {
  const isDay = isDaytime();

  // Clear sky
  if (code === 0) return isDay ? 'clear-day' : 'clear-night';

  // Mainly clear, partly cloudy
  if (code >= 1 && code <= 2)
    return isDay ? 'partly-cloudy-day' : 'partly-cloudy-night';

  // Overcast
  if (code === 3) return 'overcast';

  // Fog and depositing rime fog
  if (code === 45) return 'fog';
  if (code === 48) return 'haze';

  // Drizzle (light, moderate, dense)
  if (code >= 51 && code <= 55) return 'light-rain';

  // Freezing drizzle
  if (code >= 56 && code <= 57) return 'sleet';

  // Rain (slight, moderate)
  if (code >= 61 && code <= 63) return isDay ? 'rain-sun' : 'rain-moon';

  // Heavy rain
  if (code === 65) return 'heavy-rain';

  // Freezing rain
  if (code >= 66 && code <= 67) return 'sleet';

  // Snow fall (slight)
  if (code === 71) return 'light-snow';

  // Snow fall (moderate, heavy)
  if (code >= 73 && code <= 75) return 'blizzard';

  // Snow grains
  if (code === 77) return 'snow-showers';

  // Rain showers (slight, moderate)
  if (code >= 80 && code <= 81) return 'showers';

  // Violent rain showers
  if (code === 82) return 'heavy-rain';

  // Snow showers (slight, heavy)
  if (code >= 85 && code <= 86) return 'snow-showers';

  // Thunderstorm (slight or moderate)
  if (code === 95) return 'thunderstorm';

  // Thunderstorm with hail
  if (code >= 96 && code <= 99) return 'thunderstorm-with-rain';

  return 'mostly-cloudy';
};

export const formatTemperature = (temp: number): string => {
  const rounded = Math.round(temp);
  return rounded >= 0 ? `+${rounded}°` : `${rounded}°`;
};

export const formatTemperatureRange = (min: number, max: number): string => {
  const roundedMax = Math.round(max);
  const roundedMin = Math.round(min);
  const maxStr = roundedMax >= 0 ? `+${roundedMax}°` : `${roundedMax}°`;
  const minStr = roundedMin >= 0 ? `+${roundedMin}°` : `${roundedMin}°`;
  return `${maxStr}/${minStr}`;
};

export const formatHour = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const formatDayShort = (isoString: string): string => {
  const date = new Date(isoString);
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${dayName}, ${day} ${month}`;
};

export const getDayOfWeek = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

export const getCurrentTimeFormatted = (): string => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
